import { FeatureServices } from '@feature-hub/core';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { Logger } from '@feature-hub/logger';
import { ContentServiceV1 } from '@oneaudi/content-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { VueFormatterServiceInterfaceV1 } from '@oneaudi/vue-formatter-service';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ReactNode } from 'react';

import { Content, ContentImage } from './EditorContentTypes';

export interface Dependencies extends FeatureServices {
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'dbad:audi-vue-formatter-service': VueFormatterServiceInterfaceV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 's2:logger': Logger;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
}

/**
 * It holds a function to retrieve the State needed by the feature app when it is rendered on the server
 * It holds the State object created from the serialized state on the client
 */
export type AsyncStateHolder = (() => Promise<InitialState>) | InitialState | undefined;

export interface InitialState {
  themeScroll: ThemeScroll;
  backdropSpeed: number;
  animation: Animation;
  themeWltp: string;
  additionalText?: AdditionalText;
  readonly wltp: WltpProps[];
}

export interface Animation {
  images: ContentImage[];
  headlines: Headlines;
  numberOfImages: number;
  useEveryNthImage?: number;
  fallback: Fallback;
}

export interface Fallback extends ContentImage {
  enabled: boolean;
  alt: string;
}

export interface CustomTheme {
  name: 'light' | 'dark';
  colors: Record<string, string>;
  invertedColors: Record<string, string>;
  breakpoints: {
    s: number;
    m: number;
    l: number;
    xl: number;
    xxl: number;
  };
}

export type ThemeScroll = CustomTheme;

export type Headlines = Iterable<ReactNode>[];

export type AdditionalText = Iterable<ReactNode> | undefined;

export interface WltpProps {
  formattedConsumption?: Iterable<ReactNode> | string | undefined;
  formattedEmission?: Iterable<ReactNode> | undefined;
  formattedCo2Class?: Iterable<ReactNode> | undefined;
  formattedDischargedCo2Class?: Iterable<ReactNode> | undefined;
  formattedDischargedConsumption?: Iterable<ReactNode> | undefined;
}

export interface AppConfig {
  theme: ThemeType;
  content: Content;
}

export enum ThemeType {
  light = 'light',
  dark = 'dark',
}

export enum ScreenOrientation {
  landscape = 'landscape',
  portrait = 'portrait',
}

export enum StaticVerticalSize {
  S = 160,
  M = 200,
  L = 240,
  XL = 264,
  XXL = 254,
}

export type ImagesMap = Map<number, HTMLImageElement>;

export interface SsrWindow extends Partial<Window> {
  innerWidth: number;
  innerHeight: number;
}

export type UseWindow = Window | SsrWindow;

export type BoxContextValue = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export interface BackdropContainerInterface {
  backdropColor: string;
  padding: number;
}
